/**
 * 振り返り（日報）と、LINE へ貼る報告文。DAILY-WORKFLOW-4。
 *
 * 権限の唯一の根拠は `docs/05` §3.1「日報（営業日の振り返りコメント）」= manager 以上。
 * ★見せる／見せないは**表示の都合**で、担保はサーバの 403（絶対ルール1）。
 *
 * ★**送信しない。** コピーしただけで「報告済み」とは書かない。
 * ★報告文は**登録済みの業務データ**から作る（OCR の読取候補は使わない）。
 */
import { buildDailyReportText, type ReportSales } from "../domain/dailyReport.js";
import { ApiError } from "./api.js";

export interface DailyReportApi {
  save(comment: string): Promise<{ comment: string }>;
}

/** 画面が閉じるまで持つ編集状態。★保存していない本文を黙って捨てない */
let dirtyComment: string | null = null;
export const hasUnsavedDailyReport = (): boolean => dirtyComment !== null;
/** タブ・日付を移る前に呼ぶ。破棄を選んだら編集中の印を落とす */
export function discardDailyReportDraft(): void {
  dirtyComment = null;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  return node;
};

export function dailyReportView(input: {
  businessDate: string;
  /** 保存済みの本文。無ければ null */
  saved: string | null;
  /** 登録済みの売上。未登録なら null */
  sales: ReportSales | null;
  api: DailyReportApi;
  /** 差し替え可能にしてあるのは、クリップボードを持たない環境を検査で通すため */
  clipboard?: { writeText(text: string): Promise<void> } | undefined;
}): HTMLElement {
  dirtyComment = null;
  const section = el("section");
  section.className = "daily-report";
  section.append(el("h2", "振り返り・報告"));

  const label = el("label", "今日の振り返り");
  const area = el("textarea");
  area.rows = 4;
  area.maxLength = 2000;
  area.value = input.saved ?? "";
  label.append(area);
  section.append(label);

  const save = el("button", "振り返りを保存");
  save.type = "button";
  const status = el("p");
  status.setAttribute("role", "status");
  section.append(save, status);

  const preview = el("pre");
  preview.className = "daily-report-preview";
  const copy = el("button", "報告文をコピー");
  copy.type = "button";
  const copyStatus = el("p");
  copyStatus.setAttribute("role", "status");

  /*
   * ★★**報告文の振り返りは「保存済みの本文」から作る**（引き継ぎ §3.5）。
   *   編集中の文字をそのまま載せると、画面に出ている報告文と保存されている日報が食い違う。
   *   ★食い違いを黙って起こさないために、未保存なら報告文にも「（未保存）」と出る。
   */
  let savedComment = input.saved;
  const renderPreview = (): void => {
    preview.textContent = buildDailyReportText({
      businessDate: input.businessDate,
      sales: input.sales,
      comment: savedComment,
    });
  };
  renderPreview();

  area.addEventListener("input", () => {
    dirtyComment = area.value === (savedComment ?? "") ? null : area.value;
    status.textContent = dirtyComment === null ? "" : "編集中（未保存）";
  });

  save.addEventListener("click", () => {
    const value = area.value.trim();
    if (value === "") {
      status.textContent = "振り返りを入力してください。";
      return;
    }
    save.disabled = true;
    area.disabled = true;
    status.textContent = "保存中…";
    void input.api
      .save(value)
      .then((result) => {
        savedComment = result.comment;
        area.value = result.comment;
        dirtyComment = null;
        status.textContent = "✓ 保存しました";
        renderPreview();
      })
      .catch((error: unknown) => {
        // ★失敗しても入力は消さない（§4「保存状態」）
        status.textContent =
          error instanceof ApiError && error.status === 409
            ? "この月は締め処理が済んでいるため保存できません。"
            : "保存できませんでした。入力は残っています。もう一度お試しください。";
      })
      .finally(() => {
        save.disabled = false;
        area.disabled = false;
      });
  });

  section.append(el("h3", "LINE へ貼る報告文"), preview, copy, copyStatus);

  copy.addEventListener("click", () => {
    const clipboard = input.clipboard ?? (typeof navigator === "undefined" ? undefined : navigator.clipboard);
    /*
     * ★**コピーできなかったら、手で選べるようにする**（引き継ぎ §3.5）。
     *   ★「コピーしました」とは書かない——書くと、貼り付けたつもりの空欄が送られる。
     * ★成功しても「送信済み」「報告済み」とは書かない。送るのは利用者。
     */
    const fallback = (): void => {
      copyStatus.textContent = "コピーできませんでした。本文を選択して手でコピーしてください。";
      const range = document.createRange();
      range.selectNodeContents(preview);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    };
    if (clipboard === undefined) {
      fallback();
      return;
    }
    void clipboard
      .writeText(preview.textContent ?? "")
      .then(() => {
        copyStatus.textContent = "コピーしました。LINE に貼り付けて送信してください。";
      })
      .catch(fallback);
  });

  return section;
}
